/**
 * Rw dan ambang RT/RO milik satu sumur, diketik di dalam lembar lognya.
 *
 * Keduanya dahulu satu angka untuk seluruh proyek, di rel kiri layar LRLC.
 * Alasan keduanya pindah ke sumur ada di `petro/waterplan.py`; yang perlu
 * diketahui di sini hanyalah akibatnya bagi yang memakainya, dan akibat itu
 * ada dua.
 *
 * **Yang kosong bukan nol.** Sebuah kotak kosong berarti *belum disebutkan*,
 * dan yang berlaku di sana adalah nilai dasar di atasnya — lalu Rw proyek.
 * Nol adalah jawaban yang salah dan bukan ketiadaan jawaban: Rw nol melahirkan
 * RO nol, lalu RT/RO tak hingga di tiap sampel, lalu seluruh sumur membayar.
 * Karena itu tiap kotak di panel ini `OptionalNumberField` dan bukan
 * `NumberField`, dan bayangannya menyebutkan angka yang sedang diwarisinya —
 * bukan "—" yang tidak mengatakan dari mana angkanya datang.
 *
 * **Selang yang dipajang adalah selang yang ada, bukan yang tersimpan.**
 * Keduanya berbeda, dan yang harus terlihat adalah yang pertama: sebuah panel
 * yang hanya memajang baris yang sudah diketik seseorang adalah panel kosong
 * pada sumur yang belum pernah disentuh, tanpa satu pun tempat untuk mulai.
 * Daftarnya datang dari server bersama muatan lembarnya — lihat
 * `LrlcWaterPlan` di `types.ts` untuk sebab ia tidak disusun ulang di sini.
 *
 * **Asal angkanya ikut terbaca.** Sebuah rencana boleh membawa catatan, dan
 * yang membawanya adalah Rw yang tidak diketik di panel ini melainkan dimuat
 * dari rekap lapangan lewat `manage.py import_rw` — lengkap dengan keraguan
 * yang ditulis orang yang mengerjakan crossplotnya. Catatan itu dipajang dan
 * dikirim kembali apa adanya pada tiap suntingan: `PUT` menulis seluruh
 * rencana, jadi ruas yang tidak ikut tersimpan sebagai kosong, dan sebuah Rw
 * yang kehilangan asal-usulnya pada ketikan pertama terbaca seolah seseorang
 * sudah memeriksanya.
 *
 * Tiap suntingan langsung dikirim dan lembarnya digambar ulang. Itu bukan
 * kebiasaan yang diambil begitu saja melainkan yang sudah berlaku di seluruh
 * layar ini: tiap penggeser di rel kiri menghitung ulang seketika, dan sebuah
 * panel yang menunggu tombol "Simpan" akan memajang angka yang berbeda
 * daripada lembar di sebelahnya selama jeda itu — yaitu persis keadaan yang
 * paling mahal di sini.
 */

import { useMemo } from "react";

import type { LrlcWaterBasis, LrlcWaterPlan, LrlcWaterSegment } from "../types";
import { Notice, OptionalNumberField, Panel, Segmented } from "./ui";

/** Badan permintaan yang disimpan server; lihat `LrlcWaterPlanView`. */
export interface WaterPlanPatch {
  basis: LrlcWaterBasis;
  rw: number | null;
  rt_ro_cutoff: number | null;
  intervals: { key: string; rw: number | null; rt_ro_cutoff: number | null }[];
  /**
   * Catatan yang sudah ada, dikirim kembali apa adanya.
   *
   * Panel ini tidak menyuntingnya, dan justru itu sebabnya ia ikut: `PUT`
   * menulis seluruh rencana, jadi ruas yang tidak dikirim tersimpan sebagai
   * kosong. Rw yang dimuat dari rekap lapangan membawa kalimat tentang asalnya
   * — `manage.py import_rw` — dan tanpa baris ini kalimat itu terhapus oleh
   * ketikan pertama siapa pun di kotak di atasnya, tanpa satu tanda pun.
   */
  note: string;
}

const BASIS_LABEL: Record<LrlcWaterBasis, string> = {
  whole: "Whole depth",
  markers: "Marker to marker",
  zones: "Zone to zone",
};

/** Rencana ini sebagai badan permintaan, sebelum satu ruas pun diubah. */
function toPatch(plan: LrlcWaterPlan): WaterPlanPatch {
  return {
    basis: plan.basis,
    rw: plan.rw,
    rt_ro_cutoff: plan.rt_ro_cutoff,
    intervals: plan.segments.map((s) => ({
      key: s.key, rw: s.rw, rt_ro_cutoff: s.rt_ro_cutoff,
    })),
    note: plan.note,
  };
}

/**
 * Angka yang diwarisi sebuah kotak kosong, sebagai bayangan di dalamnya.
 *
 * Disebutkan dan tidak dibiarkan "—", sebab kedua keadaan itu terlihat sama
 * dan berarti hal yang sangat berbeda: yang satu sumur yang membaca Rw proyek,
 * yang lain sumur yang tidak punya Rw sama sekali. Yang kedua tidak pernah
 * terjadi di sini, dan sebuah kotak yang terbaca seperti itu mengirim orang
 * mencari sebab yang tidak ada.
 */
const inherited = (value: number, digits: number) => value.toFixed(digits);

export default function LrlcWaterPlanPanel({
  plan,
  wellName,
  onChange,
  readOnly,
}: {
  plan: LrlcWaterPlan;
  wellName: string;
  onChange: (patch: WaterPlanPatch) => void;
  /** Peninjau boleh membaca angkanya dan tidak boleh menggesernya. */
  readOnly?: boolean;
}) {
  const patch = useMemo(() => toPatch(plan), [plan]);
  const set = (next: Partial<WaterPlanPatch>) => onChange({ ...patch, ...next });

  const setSegment = (key: string, field: "rw" | "rt_ro_cutoff",
                      value: number | null) =>
    set({
      intervals: patch.intervals.map(
        (row) => (row.key === key ? { ...row, [field]: value } : row)),
    });

  // Dasar yang tidak punya satu pun selang tetap ditawarkan, dan justru
  // ketiadaannya yang dikatakan di bawah. Yang dipadamkan tanpa sebab terbaca
  // seperti fitur yang rusak; yang menjawab "sumur ini belum punya zona"
  // mengirim orangnya ke menu Data, yaitu tempat zona memang dibuat.
  const options = (Object.keys(BASIS_LABEL) as LrlcWaterBasis[])
    .map((value) => ({ value, label: BASIS_LABEL[value] }));

  return (
    <Panel
      title="Water & contrast"
      meta={plan.varies ? `${plan.applied.length} in use` : "one value"}
      bodyStyle={{ padding: 11, gap: 9 }}
    >
      {/* Ketiganya harus terbaca utuh, dan di rel selebar 274 px ketiganya
          tidak muat sebaris. Tanpa `wrap` yang terpotong adalah "Whole depth"
          — dari kiri, dan justru pada saat seseorang menekan "Zone to zone",
          sebab strip itu menggulir sendiri ke pilihan yang baru difokus.
          Nama dasarnya tidak dipendekkan menjadi "Markers" dan "Zones" sebab
          keduanya kemudian berbunyi seperti nama benda alih-alih nama
          pembagian: yang dipilih di sini bukan marker melainkan selang dari
          satu marker ke marker berikutnya. */}
      <Segmented
        value={plan.basis}
        size="sm"
        wrap
        options={options}
        onChange={(basis) => !readOnly && set({ basis })}
      />

      <OptionalNumberField
        label="Rw (ohm·m)"
        value={plan.rw}
        step={0.005}
        placeholder={inherited(plan.base_rw, 4)}
        onChange={(rw) => !readOnly && set({ rw })}
      />
      <OptionalNumberField
        label="RT / RO at least"
        value={plan.rt_ro_cutoff}
        step={0.1}
        placeholder={inherited(plan.base_rt_ro_cutoff, 2)}
        onChange={(rt_ro_cutoff) => !readOnly && set({ rt_ro_cutoff })}
      />
      <span style={hint}>
        {plan.basis === "whole"
          ? `Both belong to ${wellName} and are recomputed, not redrawn: Rw `
            + `moves the RO line this whole sheet compares RT against, and the `
            + `cutoff moves the boundary between water and pay. `
          : `Both belong to ${wellName}, and are what the depths below read `
            + `where no interval names its own. `}
        Leave a box empty and it reads the project's number, shown greyed
        inside it.
      </span>

      {/* Dari mana angka di kotak itu datang, bila ia tidak diketik di sini.
          Rw yang dimuat dari rekap lapangan membawa kalimat tentang asalnya —
          termasuk keraguan yang ditulis orang yang mengerjakan crossplotnya —
          dan kalimat itulah yang menjawab pertanyaan yang sebenarnya sedang
          ditanyakan orang di depan panel ini: boleh dipakai apa adanya, atau
          harus diperiksa dulu. Sebuah angka yang berdiri sendirian tanpa
          asal-usul terbaca seolah seseorang sudah memeriksanya. */}
      {plan.note ? <Notice tone="info">{plan.note}</Notice> : null}

      {plan.basis !== "whole" && (
        plan.segments.length ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {plan.segments.map((segment) => (
                <SegmentRow
                  key={segment.key}
                  segment={segment}
                  base={plan}
                  readOnly={readOnly}
                  onChange={setSegment}
                />
              ))}
            </div>
            <span style={hint}>
              Depth that no interval covers — above the topmost marker, below
              the lowest, the gap between two zones — reads the two boxes
              above. Nothing falls out of the classification for being left
              unnamed.
            </span>
          </>
        ) : (
          <Notice tone="warn">
            {plan.basis === "markers"
              ? `${wellName} carries ${plan.counts.markers === 0 ? "no marker "
                  + "pair" : "too few markers"} — an interval needs two ends. `
                + `Pick them in the Data menu, or in Correlation.`
              : `${wellName} has no zone yet. Name them in the Data menu, and `
                + `each one can carry its own water here.`}
          </Notice>
        )
      )}
    </Panel>
  );
}

function SegmentRow({
  segment,
  base,
  readOnly,
  onChange,
}: {
  segment: LrlcWaterSegment;
  base: LrlcWaterPlan;
  readOnly?: boolean;
  onChange: (key: string, field: "rw" | "rt_ro_cutoff",
             value: number | null) => void;
}) {
  return (
    <div style={row}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ font: "600 10px var(--font-sans)", color: "var(--ink-2)",
                       overflow: "hidden", textOverflow: "ellipsis",
                       whiteSpace: "nowrap" }}>
          {segment.label}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)",
                       flex: "none" }}>
          {segment.top.toFixed(1)}–{segment.base.toFixed(1)}
        </span>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <OptionalNumberField
            label="RW"
            value={segment.rw}
            step={0.005}
            placeholder={inherited(base.base_rw, 4)}
            onChange={(value) => !readOnly && onChange(segment.key, "rw", value)}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <OptionalNumberField
            label="RT/RO"
            value={segment.rt_ro_cutoff}
            step={0.1}
            placeholder={inherited(base.base_rt_ro_cutoff, 2)}
            onChange={(value) =>
              !readOnly && onChange(segment.key, "rt_ro_cutoff", value)}
          />
        </div>
      </div>
    </div>
  );
}

const row: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 5,
  padding: "7px 8px",
  background: "var(--panel-inset)",
  border: "1px solid var(--line-soft)",
  borderRadius: "var(--radius)",
};

const hint: React.CSSProperties = {
  font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)",
};
